import { Controller } from "@hotwired/stimulus";

// Drives the modal that creates/edits a CustomFieldDefinition.
// Responsibilities :
//   - auto-slugify the internal_name from the name until the user edits it
//   - block reserved internal_names client-side (reservedValue)
//   - swap the "default value" widget when the type changes
//   - hide/show the "options" fieldset for choice types
//   - couple the `required` toggle with `show_on_create`
//   - render a live preview of the field on the right panel
//   - render a live API key (custom_fields.<internal_name>)
export default class extends Controller {
  static targets = [
    "form", "submit",
    "resourceType",
    "typeGrid",
    "name", "internalName", "internalNameError",
    "helpText", "placeholder", "placeholderField",
    "defaultField", "defaultValue", "defaultWidgets",
    "optionsField",
    "showOnCreate", "required", "requiredToggle",
    "preview", "previewLabel", "previewRequired", "previewWidget", "previewHelp", "previewApiKey",
  ];
  static values = {
    types:        { type: Array, default: [] },
    choiceTypes:  { type: Array, default: [] },
    reserved:     { type: Array, default: [] },
    locked:       { type: Boolean, default: false },
  };

  // ----- Lifecycle -----

  connect() {
    this._userEditedInternalName = this.internalNameTarget.dataset.userEdited === "true";
    this._highlightSelectedType();
    this._togglePlaceholderVisibility();
    this._toggleOptionsVisibility();
    this._toggleRequiredToggleEnabled();
    this._refreshDefaultWidget();
    // Quand on edite des options (choix), la valeur par defaut peut etre
    // un select / multi-checkbox dont les options doivent rester en phase.
    this.element.addEventListener("input", (e) => {
      if (e.target.closest('[data-custom-field-options-target="row"]')) {
        this._refreshChoiceDefaultOptions();
      }
    });
    this.refreshPreview();
  }

  // ----- Inputs handlers -----

  onNameInput() {
    if (this.lockedValue) return;
    if (this._userEditedInternalName) {
      this.refreshPreview();
      return;
    }
    this.internalNameTarget.value = this._slugify(this.nameTarget.value);
    this._validateInternalName();
    this.refreshPreview();
  }

  onInternalNameInput() {
    this._userEditedInternalName = true;
    this.internalNameTarget.dataset.userEdited = "true";
    this.internalNameTarget.value = this._slugifyLight(this.internalNameTarget.value);
    this._validateInternalName();
    this.refreshPreview();
  }

  changeType() {
    this._highlightSelectedType();
    this._togglePlaceholderVisibility();
    this._toggleOptionsVisibility();
    this._refreshDefaultWidget();
    this.refreshPreview();
  }

  onDefaultInput(event) {
    const widget = event.currentTarget.dataset.defaultWidget;
    const type = this._currentType();
    if (widget !== type) return;  // ignore widgets caches

    let value = "";
    switch (type) {
      case "boolean":
        value = event.currentTarget.checked ? "1" : "0";
        break;
      case "multi_choice": {
        const checked = Array.from(
          this.defaultWidgetsTarget.querySelectorAll(
            '[data-default-widget="multi_choice"] input[type=checkbox]:checked'
          )
        ).map((c) => c.value);
        value = checked.length ? JSON.stringify(checked) : "";
        break;
      }
      default:
        value = event.currentTarget.value;
    }
    this.defaultValueTarget.value = value;
    this.refreshPreview();
  }

  onShowOnCreateChange() {
    if (!this.showOnCreateTarget.checked) {
      this.requiredTarget.checked = false;
    }
    this._toggleRequiredToggleEnabled();
    this.refreshPreview();
  }

  // ----- Internal helpers -----

  _currentType() {
    if (this.lockedValue) {
      const hidden = this.element.querySelector("input[type=hidden][name='custom_field_definition[field_type]']");
      if (hidden) return hidden.value;
    }
    const checked = this.typeGridTarget.querySelector("input[type=radio]:checked");
    return checked ? checked.value : "text";
  }

  _highlightSelectedType() {
    const current = this._currentType();
    this.typeGridTarget.querySelectorAll(".cf-type-card").forEach((card) => {
      card.classList.toggle("cf-type-card--selected", card.dataset.type === current);
    });
  }

  _togglePlaceholderVisibility() {
    if (!this.hasPlaceholderFieldTarget) return;
    const t = this._currentType();
    const hide = ["boolean", "single_choice", "multi_choice", "date"].includes(t);
    this.placeholderFieldTarget.hidden = hide;
  }

  _toggleOptionsVisibility() {
    if (!this.hasOptionsFieldTarget) return;
    const isChoice = this.choiceTypesValue.includes(this._currentType());
    this.optionsFieldTarget.hidden = !isChoice;
  }

  _toggleRequiredToggleEnabled() {
    if (!this.hasRequiredToggleTarget) return;
    const show = this.showOnCreateTarget.checked;
    this.requiredToggleTarget.classList.toggle("cf-toggle--disabled", !show);
    this.requiredTarget.disabled = !show;
  }

  // Affiche le widget de default_value adapte au type courant.
  // Les autres widgets restent dans le DOM mais sont caches et leur valeur
  // n'est pas remontee. Le hidden `defaultValue` reste la seule source verite
  // envoyee au serveur.
  _refreshDefaultWidget() {
    if (!this.hasDefaultWidgetsTarget) return;
    const type = this._currentType();
    const noDefault = ["multi_choice"].includes(type) && this._choiceOptions().length === 0;
    const activeKey = noDefault ? "__none__" : type;

    this.defaultWidgetsTarget.querySelectorAll("[data-default-widget]").forEach((w) => {
      w.hidden = w.dataset.defaultWidget !== activeKey;
    });

    // Rebuild les widgets choix a partir des options courantes.
    if (type === "single_choice" || type === "multi_choice") {
      this._refreshChoiceDefaultOptions();
    }
  }

  _choiceOptions() {
    if (!this.hasOptionsFieldTarget) return [];
    const rows = this.optionsFieldTarget.querySelectorAll('[data-custom-field-options-target="row"]');
    return Array.from(rows).map((row) => {
      const nameEl  = row.querySelector('[data-custom-field-options-target="optionName"]');
      const inameEl = row.querySelector('[data-custom-field-options-target="optionInternalName"]');
      return {
        name:  nameEl  ? nameEl.value  : "",
        iname: inameEl ? inameEl.value : "",
      };
    }).filter((o) => o.name && o.iname);
  }

  _refreshChoiceDefaultOptions() {
    if (!this.hasDefaultWidgetsTarget) return;
    const opts = this._choiceOptions();
    const currentHidden = this.defaultValueTarget.value;

    // ----- single_choice : <select> -----
    const select = this.defaultWidgetsTarget.querySelector('select[data-default-widget="single_choice"]');
    if (select) {
      const previous = select.value || currentHidden;
      select.innerHTML =
        '<option value="">Aucune valeur par defaut</option>' +
        opts.map((o) => `<option value="${this._escape(o.iname)}">${this._escape(o.name)}</option>`).join("");
      // Restaure la selection si toujours valide
      if (opts.some((o) => o.iname === previous)) {
        select.value = previous;
      }
    }

    // ----- multi_choice : checkboxes -----
    const multi = this.defaultWidgetsTarget.querySelector('[data-default-widget="multi_choice"]');
    if (multi) {
      let selectedSet = new Set();
      try {
        const parsed = currentHidden ? JSON.parse(currentHidden) : [];
        if (Array.isArray(parsed)) selectedSet = new Set(parsed.map(String));
      } catch (_e) { /* ignore */ }

      multi.innerHTML = opts.map((o) => `
        <label class="cf-default-multi__item">
          <input type="checkbox"
                 value="${this._escape(o.iname)}"
                 ${selectedSet.has(o.iname) ? "checked" : ""}
                 data-default-widget="multi_choice"
                 data-action="change->custom-field-form#onDefaultInput">
          <span>${this._escape(o.name)}</span>
        </label>
      `).join("");

      if (opts.length === 0) {
        multi.innerHTML = `<div class="cf-default-empty"><i class="fa-regular fa-circle-question"></i> Ajoute des options ci-dessous pour pouvoir choisir une valeur par defaut.</div>`;
      }
    }

    // Si le type courant est un choix mais que la valeur stockee ne fait plus
    // partie des options, on nettoie le hidden.
    const type = this._currentType();
    if (type === "single_choice" && currentHidden && !opts.some((o) => o.iname === currentHidden)) {
      this.defaultValueTarget.value = "";
    }
  }

  _slugify(text) {
    return (text || "")
      .normalize("NFD").replace(/[̀-ͯ]/g, "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "")
      .replace(/^([0-9])/, "f_$1")
      .slice(0, 60);
  }

  _slugifyLight(text) {
    // While typing manually, we accept only [a-z0-9_], don't auto-prefix.
    return (text || "").toLowerCase().replace(/[^a-z0-9_]/g, "");
  }

  _validateInternalName() {
    const v = this.internalNameTarget.value;
    let err = null;
    if (!v) {
      err = null;
    } else if (!/^[a-z][a-z0-9_]{0,62}$/.test(v)) {
      err = "Doit commencer par une lettre minuscule, [a-z0-9_].";
    } else if (this.reservedValue.includes(v)) {
      err = "Ce nom est reserve, choisis-en un autre.";
    }

    if (this.hasInternalNameErrorTarget) {
      if (err) {
        this.internalNameErrorTarget.textContent = err;
        this.internalNameErrorTarget.hidden = false;
        this.internalNameTarget.classList.add("cf-input--error");
      } else {
        this.internalNameErrorTarget.hidden = true;
        this.internalNameTarget.classList.remove("cf-input--error");
      }
    }

    if (this.hasSubmitTarget) {
      this.submitTarget.disabled = Boolean(err);
    }
  }

  // ----- Preview -----

  refreshPreview() {
    const type    = this._currentType();
    const name    = this.nameTarget.value || "Nouveau champ";
    const iname   = this.internalNameTarget.value || "internal_name";
    const placeholder = this.hasPlaceholderTarget ? this.placeholderTarget.value : "";
    const help    = this.hasHelpTextTarget ? this.helpTextTarget.value : "";
    const def     = this.hasDefaultValueTarget ? this.defaultValueTarget.value : "";
    const required = this.requiredTarget.checked && this.showOnCreateTarget.checked;

    this.previewLabelTarget.textContent = name;
    this.previewRequiredTarget.hidden = !required;

    if (this.previewApiKeyTarget) {
      this.previewApiKeyTarget.textContent = `custom_fields.${iname}`;
    }

    if (help) {
      this.previewHelpTarget.textContent = help;
      this.previewHelpTarget.hidden = false;
    } else {
      this.previewHelpTarget.hidden = true;
    }

    this.previewWidgetTarget.innerHTML = this._renderPreviewWidget(type, { placeholder, def });
  }

  _renderPreviewWidget(type, { placeholder, def }) {
    const ph = (placeholder || "").replace(/"/g, "&quot;");
    const dv = (def || "").replace(/"/g, "&quot;");
    switch (type) {
      case "boolean":
        return `
          <label class="cf-prev-toggle">
            <input type="checkbox" disabled>
            <span class="cf-prev-toggle__switch"></span>
            <span>Oui</span>
          </label>`;
      case "long_text":
        return `<textarea class="cf-prev-input" rows="3" placeholder="${ph}" disabled>${dv}</textarea>`;
      case "number":
        return `<input class="cf-prev-input" type="number" placeholder="${ph}" value="${dv}" disabled>`;
      case "date":
        return `<input class="cf-prev-input" type="date" value="${dv}" disabled>`;
      case "email":
        return `<input class="cf-prev-input" type="email" placeholder="${ph || 'nom@exemple.com'}" value="${dv}" disabled>`;
      case "url":
        return `<input class="cf-prev-input" type="url" placeholder="${ph || 'https://...'}" value="${dv}" disabled>`;
      case "phone":
        return `<input class="cf-prev-input" type="tel" placeholder="${ph || '+33 6 12 34 56 78'}" value="${dv}" disabled>`;
      case "single_choice":
      case "multi_choice":
        return this._renderChoicesPreview(type === "multi_choice");
      case "text":
      default:
        return `<input class="cf-prev-input" type="text" placeholder="${ph}" value="${dv}" disabled>`;
    }
  }

  _renderChoicesPreview(multi) {
    const rows = this.optionsFieldTarget.querySelectorAll('[data-custom-field-options-target="row"]');
    const items = [];
    rows.forEach((row) => {
      const nameEl = row.querySelector('[data-custom-field-options-target="optionName"]');
      const inameEl = row.querySelector('[data-custom-field-options-target="optionInternalName"]');
      if (nameEl && nameEl.value) {
        items.push({ name: nameEl.value, iname: inameEl ? inameEl.value : "" });
      }
    });
    if (items.length === 0) {
      return `<div class="cf-prev-empty">Ajoute au moins une option ci-dessous.</div>`;
    }
    if (multi) {
      return `
        <div class="cf-prev-multi">
          ${items.map((o) => `<label class="cf-prev-chip"><input type="checkbox" disabled> <span>${this._escape(o.name)}</span></label>`).join("")}
        </div>`;
    }
    return `
      <div class="cf-prev-pills">
        ${items.map((o) => `<button type="button" class="cf-prev-pill" disabled>${this._escape(o.name)}</button>`).join("")}
      </div>`;
  }

  _escape(s) {
    return (s || "").replace(/[<>"'&]/g, (c) => ({ "<": "&lt;", ">": "&gt;", "\"": "&quot;", "'": "&#39;", "&": "&amp;" }[c]));
  }
}
